import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	Chip,
	CircularProgress,
	Container,
	Divider,
	Grid,
	Paper,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import { getClientMenus } from '../../../services/Clint/UserOrdersApi';
import { resolveImageUrl } from '../../../services/indexMediaBaseUrl';

const UNCATEGORIZED = 'بدون تصنيف';

const toPrice = (value) => {
	const amount = Number(value || 0);
	return Number.isNaN(amount) ? '0.00' : amount.toFixed(2);
};

const groupItemsByCategory = (items = []) =>
	items.reduce((acc, item) => {
		const key = item?.category?.name || UNCATEGORIZED;
		if (!acc[key]) acc[key] = [];
		acc[key].push(item);
		return acc;
	}, {});

const isVisibleItem = (item) => Number(item?.status) !== 0;
const isPublicApprovedDrink = (drink) =>
	Number(drink?.is_approved) === 1 &&
	Number(drink?.is_public) === 1 &&
	Number(drink?.status) === 1;

const getImageUrl = (image) => (image ? resolveImageUrl(image) : '/placeholder.png');

const getCategoryImage = (items = []) => {
	const categoryImage = items[0]?.category?.image;
	const itemImage = items.find((item) => item?.image)?.image;
	return getImageUrl(categoryImage || itemImage);
};


const MenuCard = ({ item, type }) => {
	const isDrink = type === 'drink';
	const title = isDrink ? item.drink_name || item.name || 'مشروب' : item.name || 'منتج';
	const description = item.description || item.description_en || 'لا يوجد وصف';
	const imageUrl = getImageUrl(item.image);

	return (
		<Card
			elevation={0}
			sx={{
				height: '100%',
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 3,
				display: 'flex',
				flexDirection: 'column',
				transition: '0.2s ease',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: 3,
					borderColor: isDrink ? 'secondary.main' : 'primary.main',
				},
			}}
		>
			<CardMedia component="img" height="180" image={imageUrl} alt={title} sx={{ objectFit: 'cover' }} />
			<CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%' }}>
				<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
					{title}
				</Typography>

				<Typography
					variant="body2"
					color="text.secondary"
					sx={{
						minHeight: 42,
						overflow: 'hidden',
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
					}}
				>
					{description}
				</Typography>

				<Divider />

				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 'auto' }}>
					<Chip label={`${toPrice(item.price)} ج.م`} color="success" variant="outlined" sx={{ fontWeight: 700 }} />
				</Box>

				{isDrink && item.temperature ? <Chip size="small" label={item.temperature} /> : null}
			</CardContent>
		</Card>
	);
};

const CategoryCard = ({ categoryName, items, color = 'primary', icon, onClick }) => {
	const description = items[0]?.category?.description || items[0]?.category?.name_en || '';

	return (
		<Card
			elevation={0}
			onClick={onClick}
			sx={{
				height: '100%',
				cursor: 'pointer',
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 3,
				overflow: 'hidden',
				transition: '0.2s ease',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: 3,
					borderColor: `${color}.main`,
				},
			}}
		>
			<CardMedia component="img" height="150" image={getCategoryImage(items)} alt={categoryName} sx={{ objectFit: 'cover' }} />
			<CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.35 }} noWrap>
						{categoryName}
					</Typography>
					<Box sx={{ color: `${color}.main`, display: 'flex', alignItems: 'center' }}>
						{icon}
					</Box>
				</Box>

				{description ? (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							minHeight: 40,
							overflow: 'hidden',
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
						}}
					>
						{description}
					</Typography>
				) : null}

				<Chip
					size="small"
					color={color}
					variant="outlined"
					label={`${items.length} عنصر`}
					sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
				/>
			</CardContent>
		</Card>
	);
};

const CategoryHeader = ({ title, color = 'primary', onBack }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
		<Button
			variant="outlined"
			color={color}
			startIcon={<ArrowBackIcon />}
			onClick={onBack}
			sx={{ borderRadius: 2, fontWeight: 800 }}
		>
			التصنيفات
		</Button>
		<Typography variant="h6" sx={{ fontWeight: 800 }} noWrap>
			{title}
		</Typography>
	</Box>
);

const Home = () => {
	const [products, setProducts] = useState([]);
	const [drinks, setDrinks] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [activeTab, setActiveTab] = useState(0);
	const [selectedProductCategory, setSelectedProductCategory] = useState('');

	const groupedProducts = useMemo(() => groupItemsByCategory(products), [products]);

	const fetchMenus = async () => {
		setLoading(true);
		setError('');
		try {
			const response = await getClientMenus();
			setProducts((response?.data?.products || []).filter(isVisibleItem));
			setDrinks((response?.data?.drinks || []).filter(isPublicApprovedDrink));
		} catch (err) {
			setError(err?.message || 'حدث خطأ أثناء جلب المنيو');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		document.title = 'Nuvvy - المنيو';
		fetchMenus();
	}, []);

	const renderCategoryGrid = (entries) => {
		return (
			<Grid container spacing={2} sx={{ mt: 2 }}>
				{entries.map(([categoryName, items]) => (
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={`product-category-${categoryName}`}>
						<CategoryCard
							categoryName={categoryName}
							items={items}
							color="primary"
							icon={<LocalDiningIcon />}
							onClick={() => setSelectedProductCategory(categoryName)}
						/>
					</Grid>
				))}
			</Grid>
		);
	};

	const renderItemsGrid = (items, type) => (
		<Grid container spacing={2}>
			{items.map((item) => (
				<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={`${type}-${item.id}`}>
					<MenuCard item={item} type={type} />
				</Grid>
			))}
		</Grid>
	);

	return (
		<Box
			dir="rtl"
			lang="ar"
			sx={{
				minHeight: '100vh',
				py: { xs: 3, md: 5 },
				background: 'radial-gradient(circle at 0% 0%, #eef4ff 0%, #f8fbff 35%, #f9f6f1 100%)',
			}}
		>
			<Container maxWidth="lg">
				<Paper
					elevation={0}
					sx={{
						p: { xs: 2, md: 3 },
						mb: 3,
						borderRadius: 4,
						border: '1px solid',
						borderColor: 'divider',
						background:
							'linear-gradient(130deg, rgba(20,118,255,0.08) 0%, rgba(255,153,71,0.12) 60%, rgba(255,255,255,1) 100%)',
					}}
				>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
						<Box>
							<Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
								منيو NUVVY - تجربة لا تنسى
							</Typography>
							<Typography color="text.secondary">
								نفتخر في NUVVY أننا أول كافيه في مصر يوفر للعميل تجربة إنشاء مشروب مخصص له.
							</Typography>
						</Box>
					</Box>
				</Paper>

				{error ? (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				) : null}

				<Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
					<Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="fullWidth">
						<Tab icon={<LocalDiningIcon />} iconPosition="start" label={`منيو (${products.length})`} />
						<Tab icon={<LocalCafeIcon />} iconPosition="start" label={`تم صنعها بواسطة العملاء (${drinks.length})`} />
					</Tabs>
				</Paper>

				{loading ? (
					<Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center' }}>
						<CircularProgress />
					</Box>
				) : null}

				{!loading && activeTab === 0 ? (
					Object.keys(groupedProducts).length === 0 ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							لا توجد أصناف حاليًا.
						</Alert>
					) : !selectedProductCategory ? (
						renderCategoryGrid(Object.entries(groupedProducts))
					) : (
						<Box sx={{ mt: 2 }}>
							<CategoryHeader
								title={selectedProductCategory}
								color="primary"
								onBack={() => setSelectedProductCategory('')}
							/>
							{renderItemsGrid(groupedProducts[selectedProductCategory] || [], 'product')}
						</Box>
					)
				) : null}

				{!loading && activeTab === 1 ? (
					drinks.length === 0 ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							لا توجد مشروبات حاليًا.
						</Alert>
					) : (
						<Box sx={{ mt: 2 }}>
							{renderItemsGrid(drinks, 'drink')}
						</Box>
					)
				) : null}
			</Container>
		</Box>
	);
};

export default Home;
